import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { contentAPI } from "../api/endpoints";
import ContentCard from "../components/ContentCard";
import { FiArrowLeft, FiLoader } from "react-icons/fi";

const SKELETON_COUNT = 12;

function CardSkeleton() {
    return (
        <div className="flex flex-col">
            <div className="w-full aspect-[2/3] bg-dark-800 rounded-xl animate-pulse" />
            <div className="w-3/4 h-4 bg-dark-700 rounded mt-3 animate-pulse" />
            <div className="w-1/2 h-3 bg-dark-700 rounded mt-2 animate-pulse" />
        </div>
    );
}

export default function CategoryPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const type = searchParams.get("type") || "movie";
    const title = searchParams.get("title") || "";
    const contentTypeParam = searchParams.get("content_type") || "movie";
    const isSeries = contentTypeParam === "series";

    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const loaderRef = useRef(null);

    const fetchPage = useCallback(
        async (pageNum) => {
            try {
                const res = await contentAPI.getMore(type, title, pageNum, isSeries);
                const data = res.data;

                // API may return array directly or { data: [...] }
                const newItems = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.data)
                        ? data.data
                        : [];

                if (newItems.length === 0) {
                    setHasMore(false);
                } else {
                    setItems((prev) => (pageNum === 1 ? newItems : [...prev, ...newItems]));
                }
            } catch {
                setHasMore(false);
            }
        },
        [type, title, isSeries]
    );

    // Initial load
    useEffect(() => {
        setItems([]);
        setPage(1);
        setHasMore(true);
        setLoading(true);
        fetchPage(1).finally(() => setLoading(false));
    }, [type, title, fetchPage]);

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        const el = loaderRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    setLoadingMore(true);
                    fetchPage(nextPage).finally(() => setLoadingMore(false));
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loading, page, fetchPage]);

    const contentType = isSeries ? "series" : "movie";

    return (
        <div className="min-h-screen bg-dark-900 pt-20 pb-16">
            {/* Header */}
            <div className="px-5 md:px-10 max-w-[1400px] mx-auto mb-8">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-5 transition-colors"
                >
                    <FiArrowLeft size={16} /> Back
                </button>

                <h1 className="text-3xl font-bold text-white">{title}</h1>
                <p className="text-gray-500 text-sm mt-1">
                    {items.length > 0 && !loading
                        ? `Showing ${items.length}${hasMore ? "+" : ""} titles`
                        : "Loading content..."}
                </p>
            </div>

            {/* Grid */}
            <div className="px-5 md:px-10 max-w-[1400px] mx-auto">
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {[...Array(SKELETON_COUNT)].map((_, i) => (
                            <CardSkeleton key={i} />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">
                        <p className="text-lg">No content found in this category</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {items.map((item, i) => (
                                <ContentCard
                                    key={`${item.id ?? i}-${i}`}
                                    item={item}
                                    type={contentType}
                                    fillWidth
                                />
                            ))}
                        </div>

                        {/* Infinite scroll sentinel */}
                        <div ref={loaderRef} className="flex justify-center py-10">
                            {loadingMore && (
                                <div className="flex items-center gap-2 text-gray-400 text-sm">
                                    <FiLoader className="animate-spin" size={18} />
                                    Loading more...
                                </div>
                            )}
                            {!hasMore && items.length > 0 && (
                                <p className="text-gray-600 text-sm">You've reached the end</p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
